import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import Header from '@/components/Header';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import Footer from '@/components/Footer';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { usePolicyAcknowledgment } from '@/hooks/usePolicyAcknowledgment';
import { policyAcknowledgmentService } from '@/services/policyAcknowledgmentService';
import { 
  FileText, 
  Shield, 
  Cookie, 
  Users, 
  CheckCircle2, 
  ArrowLeft,
  Download,
  Calendar,
  AlertCircle,
  HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function PolicyCenter() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user: unifiedUser } = useUnifiedUser();
  const { acknowledgmentData, isLoading } = usePolicyAcknowledgment(
    unifiedUser?.id || null,
    unifiedUser?.userType || null
  );

  const [privacyPolicy, setPrivacyPolicy] = useState<string>('');
  const [termsOfService, setTermsOfService] = useState<string>('');
  const [loadingPolicies, setLoadingPolicies] = useState(true);

  useEffect(() => {
    loadPolicyContent();
  }, []);

  const loadPolicyContent = async () => {
    setLoadingPolicies(true);
    try {
      const [ppResult, tosResult] = await Promise.all([
        policyAcknowledgmentService.getPolicyContent('privacy_policy'),
        policyAcknowledgmentService.getPolicyContent('terms_of_service'),
      ]);

      setPrivacyPolicy(ppResult.data?.content || 'Privacy Policy content will be available soon.');
      setTermsOfService(tosResult.data?.content || 'Terms of Service content will be available soon.');
    } catch (error) {
      console.error('Error loading policies:', error);
      toast.error('Failed to load policy content');
    } finally {
      setLoadingPolicies(false);
    }
  };

  const downloadPolicy = (policyName: string, content: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${policyName}-${new Date().toISOString().split('T')[0]}.txt`;
    link.click();
    toast.success(`${policyName} downloaded`);
  };

  const faqs = [
    {
      question: 'Why do I need to acknowledge these policies?',
      answer: 'We are legally required to obtain your consent before collecting and processing your personal data. This ensures transparency and protects your privacy rights under GDPR, CCPA, and other privacy regulations.',
    },
    {
      question: 'What happens if I don\'t accept the policies?',
      answer: 'You will not be able to use the Players Lockeroom platform until you review and accept our Privacy Policy and Terms of Service. These agreements are essential for providing our services.',
    },
    {
      question: 'Can I withdraw my consent?',
      answer: 'Yes, you can withdraw your consent at any time by contacting our support team. However, this may limit or prevent your access to certain features or the entire platform.',
    },
    {
      question: 'What is COPPA and why does it matter?',
      answer: 'COPPA (Children\'s Online Privacy Protection Act) is a U.S. federal law that protects the privacy of children under 13. If you are under 13, we require verifiable parental consent before you can use our services.',
    },
    {
      question: 'How do you use cookies?',
      answer: 'We use cookies to improve your experience, remember your preferences, and analyze site usage. You can manage your cookie preferences in your privacy settings.',
    },
    {
      question: 'What are my data rights?',
      answer: 'You have the right to access, correct, delete, or export your personal data. You also have the right to object to certain processing activities. Contact us to exercise these rights.',
    },
    {
      question: 'Will I be notified if policies change?',
      answer: 'Yes, we will notify you via email when we make significant changes to our policies. You will need to review and reacknowledge the updated policies to continue using the platform.',
    },
    {
      question: 'How is my data protected?',
      answer: 'We implement industry-standard security measures including encryption, secure servers, and regular security audits to protect your personal information.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {isMobile ? <MobileHeader /> : <Header />}
      
      <main className={`${isMobile ? 'pt-20 pb-20 px-4' : 'pt-28 pb-6 px-4 md:px-6'}`}>
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <div className="mb-6">
            <Button 
              onClick={() => navigate(-1)}
              variant="outline"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Policy Center</h1>
            <p className="text-muted-foreground">
              Review our policies, your acknowledgment status, and frequently asked questions
            </p>
          </div>

          {/* Acknowledgment Status Card */}
          {acknowledgmentData && (
            <Card className="mb-6 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Your Acknowledgment Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-primary" />
                      <span className="font-medium">Privacy Policy</span>
                    </div>
                    <Badge variant={acknowledgmentData.privacy_policy_accepted ? "default" : "secondary"}>
                      {acknowledgmentData.privacy_policy_accepted ? 'Accepted' : 'Pending'}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="font-medium">Terms of Service</span>
                    </div>
                    <Badge variant={acknowledgmentData.tos_accepted ? "default" : "secondary"}>
                      {acknowledgmentData.tos_accepted ? 'Accepted' : 'Pending'}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <Cookie className="h-4 w-4 text-primary" />
                      <span className="font-medium">Cookie Policy</span>
                    </div>
                    <Badge variant={acknowledgmentData.cookies_accepted ? "default" : "secondary"}>
                      {acknowledgmentData.cookies_accepted ? 'Accepted' : 'Pending'}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span className="font-medium">Acknowledged</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {new Date(acknowledgmentData.acknowledged_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {acknowledgmentData.needs_reacknowledgment && (
                  <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-500">Reacknowledgment Required</p>
                      <p className="text-sm text-muted-foreground">
                        Our policies have been updated. Please review and reacknowledge them.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Main Content Tabs */}
          <Tabs defaultValue="policies" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="policies">Policy Documents</TabsTrigger>
              <TabsTrigger value="faq">FAQ</TabsTrigger>
            </TabsList>

            <TabsContent value="policies" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Privacy Policy
                  </CardTitle>
                  <CardDescription>
                    Version {acknowledgmentData?.privacy_policy_version || '1.0'} • 
                    Learn how we collect, use, and protect your personal information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="prose prose-sm max-w-none">
                    {loadingPolicies ? (
                      <p className="text-muted-foreground">Loading policy...</p>
                    ) : (
                      <div className="whitespace-pre-wrap">{privacyPolicy}</div>
                    )}
                  </div>
                  <Button
                    onClick={() => downloadPolicy('Privacy-Policy', privacyPolicy)}
                    variant="outline"
                    className="w-full md:w-auto"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Privacy Policy
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Terms of Service
                  </CardTitle>
                  <CardDescription>
                    Version {acknowledgmentData?.tos_version || '1.0'} • 
                    Understand the rules and guidelines for using our platform
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="prose prose-sm max-w-none">
                    {loadingPolicies ? (
                      <p className="text-muted-foreground">Loading terms...</p>
                    ) : (
                      <div className="whitespace-pre-wrap">{termsOfService}</div>
                    )}
                  </div>
                  <Button
                    onClick={() => downloadPolicy('Terms-of-Service', termsOfService)}
                    variant="outline"
                    className="w-full md:w-auto"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Terms of Service
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Compliance Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="p-4 bg-muted rounded-lg">
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <Badge>GDPR</Badge>
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        We comply with EU General Data Protection Regulation standards for data privacy and security.
                      </p>
                    </div>

                    <div className="p-4 bg-muted rounded-lg">
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <Badge>CCPA</Badge>
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        California Consumer Privacy Act rights are fully supported for all eligible users.
                      </p>
                    </div>

                    <div className="p-4 bg-muted rounded-lg">
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <Badge>COPPA</Badge>
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Children's Online Privacy Protection Act requirements are enforced for users under 13.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="faq" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5" />
                    Frequently Asked Questions
                  </CardTitle>
                  <CardDescription>
                    Find answers to common questions about our policies and your privacy
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {faqs.map((faq, index) => (
                      <AccordionItem key={index} value={`item-${index}`}>
                        <AccordionTrigger className="text-left">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Need More Help?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    If you have questions not covered in our FAQ, please contact us:
                  </p>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <strong>Email:</strong> privacy@playerslockeroom.com
                    </p>
                    <p className="text-sm">
                      <strong>Support:</strong> support@playerslockeroom.com
                    </p>
                    <p className="text-sm">
                      <strong>Data Protection Officer:</strong> dpo@playerslockeroom.com
                    </p>
                  </div>
                  <Button variant="outline" className="w-full md:w-auto">
                    Contact Support
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      {!isMobile && <Footer />}
      <MobileBottomNav />
    </div>
  );
}
